import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import InfiniteLoader from '@/components/InfiniteLoader';
import { PageHeading, SrOnly, TeamHeading } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useSortState from '@/hooks/useSortState';
import {
  PaginatedResponse,
  SubtitlesGenerationProject,
} from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import Form from './components/Form';
import TableRow from './components/TableRow';

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SectionHeader = styled.div<{ $pushRight?: boolean }>`
  display: flex;
  align-items: flex-end;
  margin: 0 0 12px;
  ${({ $pushRight }) =>
    $pushRight &&
    css`
      justify-content: flex-end;
    `}
`;

const ViewControls = styled(Controls)`
  margin-right: auto;
`;

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: top;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

const StyledButton = styled(Button)`
  background: transparent;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const SubtitlesGenerationProjects = () => {
  const [showCreationForm, setShowCreationForm] = useState(false);
  const [search, setSearch] = useState('');
  const { status, teamId } = useParams();
  const debouncedSearch = useDebounce(search, 500);
  const { sort, setSort, sortQueryParamObj } = useSortState({
    direction: SortDirection.DESC,
    property: 'createdAt',
  });

  const navigate = useNavigate();

  const fetchPosts = ({
    pageParam = '/v1/distribution/subtitles-generation-projects',
  }) => {
    return api.get<never, PaginatedResponse<SubtitlesGenerationProject>>(
      pageParam,
      {
        params: {
          ...sortQueryParamObj,
          'filter[status]': status,
          ...(!!debouncedSearch && {
            'filter[search]': debouncedSearch,
          }),
        },
      },
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    [
      'subtitles_generation_projects',
      {
        status,
        sort,
        ...(!!debouncedSearch && {
          search: debouncedSearch,
        }),
      },
    ],
    fetchPosts,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 90 * 1000,
    },
  );

  const projects = useMemo(
    () =>
      data?.pages?.reduce<SubtitlesGenerationProject[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data],
  );

  if (!['pending', 'done'].includes(status ?? '')) {
    return <Navigate to="/404" replace />;
  }

  return (
    <>
      <TeamHeading />
      <HeadingRow>
        <PageHeading>Subtitles Generator</PageHeading>
        <HeaderButtons>
          <StyledButton
            onClick={() => navigate('../prompts', { relative: 'path' })}
            color="secondary"
          >
            Prompts
          </StyledButton>
          <Button onClick={() => setShowCreationForm(true)}>
            Create project
          </Button>
        </HeaderButtons>
      </HeadingRow>
      <SectionHeader>
        <ViewControls>
          <ControlsButton
            active={status === 'pending'}
            onClick={() =>
              navigate(
                `/teams/${teamId}/distribution/subtitles-generator/pending`,
              )
            }
          >
            Pending
          </ControlsButton>
          <ControlsButton
            active={status === 'done'}
            onClick={() =>
              navigate(`/teams/${teamId}/distribution/subtitles-generator/done`)
            }
          >
            Done
          </ControlsButton>
        </ViewControls>
      </SectionHeader>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by title or project ID"
      />
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Thumb & Title</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="createdAt"
                title="Date Created"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="status"
                title="Project Status"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="videoStatus"
                title="Video Status"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="projectId"
                title="Project ID"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="freshId"
                title="Fresh ID"
              />
              <Th>Prompt</Th>
              <Th>Owner</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!projects?.length}
            >
              {projects?.map((project) => (
                <TableRow key={project.id} project={project} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
      <AnimatePresence>
        {showCreationForm && (
          <Form onClose={() => setShowCreationForm(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default SubtitlesGenerationProjects;
